/**
 * TransmittanceLut — Hillaire 2020 transmittance look-up table.
 *
 * The transmittance LUT stores, for every (altitude r, sun/view-zenith
 * cosine µ) pair, the colour transmittance T(r, µ) = exp(−τ) along the ray
 * that starts at radius `r`, travels with cosine `µ` relative to the local
 * "up", and exits through the TOP of the atmosphere. τ is the optical depth
 * (km⁻¹ · km) integrated through the Rayleigh + Mie + ozone extinction.
 *
 *   Hillaire 2020, §4, eq. (1)/(2):
 *     T(p_a → p_b) = exp( − ∫ σ_t(s) ds )
 *
 * Parameterization: §4 uses the Bruneton 2017 remap of (r, µ) → UV, which is
 * implemented (and unit-tested) in `../lutParameterization.ts`. We bake on
 * the CPU into a Float RGBA `DataTexture` of size 256×64 (W×H = µ×r), which
 * is the canonical Hillaire size. CPU baking (rather than a GPU fragment
 * pass) keeps the math identical between the GPU sampler and the CPU
 * reference used by tests, and works on both WebGPU and the WebGL2 fallback
 * — matching the engine's existing CloudNoiseGenerator / BlueNoise pattern.
 *
 * The bake itself is a single ray-march per texel (40 steps): at 256×64 that
 * is ~655 k extinction evaluations, ~3–6 ms on a modern laptop. It only
 * re-runs when the atmosphere config changes (see SkyLutSet dirty-flagging),
 * never per-frame.
 *
 * Reference:
 *   Sébastien Hillaire, "A Scalable and Production Ready Sky and Atmosphere
 *   Rendering Technique", EGSR 2020, §4 (Transmittance).
 *   github.com/sebh/UnrealEngineSkyAtmosphere (TransmittanceLutPS).
 */

import * as THREE from 'three/webgpu';
import type { AtmosphereConfig } from '../AtmosphereConfig.js';
import { computeDensities } from '../AtmosphereConfig.js';
import { transmittanceUVInverse, rayAtmosphereIntersect } from '../lutParameterization.js';
import type { Vec3 } from '../AtmosphereConfig.js';

/** Canonical Hillaire transmittance LUT size: width = µ axis, height = r axis. */
export const TRANSMITTANCE_LUT_WIDTH = 256;
export const TRANSMITTANCE_LUT_HEIGHT = 64;

/** Ray-march steps per texel for the optical-depth integral. */
export const TRANSMITTANCE_LUT_SAMPLES = 40;

/**
 * Pure-TS optical-depth integral from `radius` (km from planet centre) along
 * the ray with cosine `cosZenith` until it exits through the top of the
 * atmosphere. Returns the per-channel optical depth τ (unitless) — caller
 * exponentiates the negative to get transmittance.
 *
 * This is the CPU reference mirror used by both the bake and the unit tests.
 * Trapezoidal-style midpoint integration with `samples` segments.
 */
export function opticalDepthToTopOfAtmosphere(
  cfg: AtmosphereConfig,
  radius: number,
  cosZenith: number,
  samples = TRANSMITTANCE_LUT_SAMPLES,
): Vec3 {
  const tMax = rayAtmosphereIntersect(radius, cosZenith, cfg.topRadius);
  if (tMax <= 0) return { x: 0, y: 0, z: 0 };

  const dt = tMax / samples;
  let tauX = 0, tauY = 0, tauZ = 0;

  for (let i = 0; i < samples; i++) {
    // Midpoint of segment i.
    const t = (i + 0.5) * dt;
    // Exact radius at parameter t along the ray:
    //   r(t) = sqrt(r0² + 2·r0·µ·t + t²)
    const rAtSample = Math.sqrt(
      radius * radius + 2.0 * radius * cosZenith * t + t * t,
    );
    const altitudeKm = rAtSample - cfg.bottomRadius;
    const d = computeDensities(cfg, altitudeKm);
    // σ_t = Rayleigh·scattering + Mie·extinction + ozone·extinction
    tauX += (cfg.rayleighScattering.x * d.rayleigh
      + cfg.mieExtinction.x * d.mie
      + cfg.ozoneExtinction.x * d.ozone) * dt;
    tauY += (cfg.rayleighScattering.y * d.rayleigh
      + cfg.mieExtinction.y * d.mie
      + cfg.ozoneExtinction.y * d.ozone) * dt;
    tauZ += (cfg.rayleighScattering.z * d.rayleigh
      + cfg.mieExtinction.z * d.mie
      + cfg.ozoneExtinction.z * d.ozone) * dt;
  }
  return { x: tauX, y: tauY, z: tauZ };
}

/**
 * CPU reference: transmittance (exp(−τ)) from `radius` along `cosZenith` to
 * the top of the atmosphere. Each channel ∈ (0, 1].
 */
export function transmittanceToTopOfAtmosphere(
  cfg: AtmosphereConfig,
  radius: number,
  cosZenith: number,
  samples = TRANSMITTANCE_LUT_SAMPLES,
): Vec3 {
  const tau = opticalDepthToTopOfAtmosphere(cfg, radius, cosZenith, samples);
  return { x: Math.exp(-tau.x), y: Math.exp(-tau.y), z: Math.exp(-tau.z) };
}

/**
 * Bilinearly sample a baked transmittance LUT Float32Array at UV ∈ [0,1]².
 * Mirrors the GPU `texture()` LinearFilter + ClampToEdge behaviour so the
 * multi-scatter / sky-view CPU references read the LUT exactly like the GPU.
 */
export function sampleTransmittanceLut(
  data: Float32Array,
  width: number,
  height: number,
  u: number,
  v: number,
): Vec3 {
  // ClampToEdge + half-texel centring (texel centres at (i+0.5)/N).
  const fx = clamp01(u) * width - 0.5;
  const fy = clamp01(v) * height - 0.5;
  const x0 = Math.floor(fx); const y0 = Math.floor(fy);
  const tx = fx - x0; const ty = fy - y0;
  const cx0 = clampInt(x0, 0, width - 1);
  const cx1 = clampInt(x0 + 1, 0, width - 1);
  const cy0 = clampInt(y0, 0, height - 1);
  const cy1 = clampInt(y0 + 1, 0, height - 1);

  const c00 = texel(data, width, cx0, cy0);
  const c10 = texel(data, width, cx1, cy0);
  const c01 = texel(data, width, cx0, cy1);
  const c11 = texel(data, width, cx1, cy1);

  return {
    x: lerp(lerp(c00.x, c10.x, tx), lerp(c01.x, c11.x, tx), ty),
    y: lerp(lerp(c00.y, c10.y, tx), lerp(c01.y, c11.y, tx), ty),
    z: lerp(lerp(c00.z, c10.z, tx), lerp(c01.z, c11.z, tx), ty),
  };
}

/** Result of a transmittance bake: raw data + a ready-to-sample texture. */
export interface TransmittanceLutResult {
  data: Float32Array;
  width: number;
  height: number;
  texture: THREE.DataTexture;
}

/**
 * Bake the transmittance LUT into a Float RGBA DataTexture (256×64). Each
 * texel stores the transmittance to the top of the atmosphere for the
 * (r, µ) pair decoded from its UV via `transmittanceUVInverse`.
 */
export function bakeTransmittanceLut(
  cfg: AtmosphereConfig,
  width = TRANSMITTANCE_LUT_WIDTH,
  height = TRANSMITTANCE_LUT_HEIGHT,
  samples = TRANSMITTANCE_LUT_SAMPLES,
): TransmittanceLutResult {
  const data = new Float32Array(width * height * 4);
  for (let y = 0; y < height; y++) {
    const v = (y + 0.5) / height;
    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      const { radius, cosViewZenith } = transmittanceUVInverse(cfg, u, v);
      const t = transmittanceToTopOfAtmosphere(cfg, radius, cosViewZenith, samples);
      const i = (y * width + x) * 4;
      data[i + 0] = t.x;
      data[i + 1] = t.y;
      data[i + 2] = t.z;
      data[i + 3] = 1.0;
    }
  }
  const texture = makeFloatTexture(data, width, height, 'SkyTransmittanceLUT');
  return { data, width, height, texture };
}

// ─── Shared texture / math helpers (used by all CPU LUT bakers) ──────────────

/** Build a clamped, linear-filtered Float RGBA DataTexture from raw data. */
export function makeFloatTexture(
  data: Float32Array, width: number, height: number, name: string,
): THREE.DataTexture {
  const tex = new THREE.DataTexture(data, width, height, THREE.RGBAFormat, THREE.FloatType);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  tex.name = name;
  return tex;
}

/**
 * Distance to the NEAREST positive intersection of a ray (origin radius `r`,
 * up-cosine `mu`) with a sphere of `sphereRadius`, or -1 if the ray misses or
 * only intersects behind the origin.
 *
 * This is distinct from `rayAtmosphereIntersect`, which returns the FARTHEST
 * root (the atmosphere-shell exit). For finding where a downward view/sample
 * ray first hits the GROUND we need the nearest root — using the far root
 * would march the ray straight through the planet and sample non-physical
 * sub-surface densities (which blow up the exponential extinction).
 */
export function rayNearIntersect(
  r: number, mu: number, sphereRadius: number,
): number {
  const b = r * mu;
  const c = r * r - sphereRadius * sphereRadius;
  const disc = b * b - c;
  if (disc < 0) return -1;
  const sq = Math.sqrt(disc);
  const t0 = -b - sq;
  const t1 = -b + sq;
  if (t0 > 0) return t0;
  if (t1 > 0) return t1;
  return -1;
}

export function clamp01(x: number): number { return x < 0 ? 0 : (x > 1 ? 1 : x); }
export function clampInt(x: number, lo: number, hi: number): number {
  return x < lo ? lo : (x > hi ? hi : x);
}
export function lerp(a: number, b: number, t: number): number { return a + (b - a) * t; }

function texel(data: Float32Array, width: number, x: number, y: number): Vec3 {
  const i = (y * width + x) * 4;
  return { x: data[i], y: data[i + 1], z: data[i + 2] };
}
